import type { ObjectType } from "../hooks/useObjectType";

export type AreaLogic = {
  canAddCracks: boolean;
  canAddSubareas: boolean;
  suggestionsForLevel: number;
};

// A root area is "Aussen" when its title says so; every descendant inherits
// that from its root ancestor.
export const deriveRootAreaType = (title: unknown): string =>
  String(title ?? "").match(/aussen/i) ? "outdoor" : "indoor";

// Pure form of useAreaLogic, so the drag-and-drop projection can evaluate a
// prospective level without being inside a component.
export const getAreaLogic = (
  objectType: ObjectType | null,
  level: number,
  rootAreaType: string | undefined
): AreaLogic => {
  let canAddCracks = false;
  let canAddSubareas = false;
  let suggestionsForLevel = 1;

  switch (objectType) {
    case "familyHome":
      if (rootAreaType === "outdoor") {
        canAddCracks = level === 2;
        canAddSubareas = level <= 1;

        if (level === 2) {
          suggestionsForLevel = 4;
        }
      } else {
        canAddCracks = level === 3;
        canAddSubareas = level <= 2;

        if (level === 3) {
          suggestionsForLevel = 4;
        } else {
          suggestionsForLevel = level;
        }
      }

      break;

    case "apartmentHouse":
      if (rootAreaType === "outdoor") {
        canAddCracks = level === 2;
        canAddSubareas = level <= 1;

        if (level === 2) {
          suggestionsForLevel = 4;
        }
      } else {
        canAddCracks = level === 4;
        canAddSubareas = level <= 3;
        suggestionsForLevel = level;
      }

      break;

    // Both street types share the same area structure: a single root area that
    // gets split into sections ("Strasse Ost", "Trottoir Nord", "Mast 57", …),
    // with the cracks on those sections. They differ only in how the protocol
    // renders their photos, not in how areas are nested.
    //
    // This used to be an accident: until 2026-08 "street" had no break and fell
    // through to "streetWithDetailPhotos". Objects captured before then have
    // their cracks on the root area instead and were migrated to match.
    case "street":
    case "streetWithDetailPhotos":
      canAddCracks = level === 2;
      canAddSubareas = level <= 1;
      suggestionsForLevel = 1;

      break;
  }

  return { canAddSubareas, canAddCracks, suggestionsForLevel };
};
